// Alcance de una promo de venta: Tipo, Gama, SKU y Tienda. Sin nada elegido en una dimensión aplica
// a todo; los conteos salen del catálogo de productos cargado.
import { useFormContext, useWatch } from 'react-hook-form';
import type { CatalogProduct } from '@/api/services/retail-catalog/retail-catalog.client';
import type { Store } from '@/api/services/retail-org/retail-org.client';
import {
    RetailerScopeColumn,
    ScopeChip,
    ScopeChipGrid,
    ScopeChipGroup,
    ScopeChipStatic,
    type RetailerScopeColumnProps,
} from '@/shared/patterns/ScopeChips';
import type { SourceState } from '../api/usePromotionFormSources';
import { MULTI_RETAILER_COPY, SALE_PROMOTION_HELP } from '../constants/helpText';
import { describeScope } from '../lib/promotionsList';
import type { SalePromotionFormValues } from '../lib/salePromotionForm';
import {
    PRODUCT_TIER_OPTIONS,
    PRODUCT_TIER_TONE,
    SCOPE_CATEGORY_OPTIONS,
} from '@/shared/domain/productScope';
import {
    MISSING_CHANNEL,
    MISSING_PRODUCT_TYPE,
    MISSING_PRODUCT_TYPES,
} from '../lib/unsupportedFeatures';
import { AllScopeChip, SkuScopeGroup, StoreScopeGroup, toggleIn } from './ScopePickers';

const MULTI_NOTE = 'mt-1.5 text-[10.5px] text-muted-foreground';

function countBy<K extends string>(
    products: readonly CatalogProduct[],
    keyOf: (product: CatalogProduct) => K | null | undefined,
): Map<K, number> {
    const counts = new Map<K, number>();
    for (const product of products) {
        const key = keyOf(product);
        if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
}

export function SalePromotionScopeEditor({
    retailer,
    multiChain = false,
    products,
    productsState,
    stores,
    storesState,
}: {
    retailer?: RetailerScopeColumnProps;
    /** Varias cadenas: SKU y tienda no se eligen porque son de una sola. */
    multiChain?: boolean;
    products: CatalogProduct[];
    productsState: SourceState;
    stores: Store[];
    storesState: SourceState;
}) {
    const { control, setValue } = useFormContext<SalePromotionFormValues>();
    const [categories, tiers, skus, storeIds] = useWatch({
        control,
        name: ['productCategories', 'tiers', 'skus', 'storeIds'],
    });
    const catalogReady = productsState === 'ready' && !multiChain;
    const categoryCounts = countBy(products, (product) => product.category);
    const tierCounts = countBy(products, (product) => product.tier);
    const dirty = { shouldDirty: true } as const;

    return (
        <ScopeChipGrid columns={retailer ? 5 : 4}>
            {retailer ? <RetailerScopeColumn {...retailer} /> : null}
            <ScopeChipGroup title="Tipo" help={SALE_PROMOTION_HELP.scopeType}>
                <AllScopeChip
                    dim="tier"
                    label="Todos"
                    selected={categories.length === 0}
                    onClear={() => {
                        setValue('productCategories', [], dirty);
                    }}
                />
                {SCOPE_CATEGORY_OPTIONS.map((option) => (
                    <ScopeChip
                        key={option.value}
                        dim="tier"
                        label={option.label}
                        count={catalogReady ? (categoryCounts.get(option.value) ?? 0) : undefined}
                        selected={categories.includes(option.value)}
                        onToggle={() => {
                            setValue(
                                'productCategories',
                                toggleIn(categories, option.value),
                                dirty,
                            );
                        }}
                    />
                ))}
                {MISSING_PRODUCT_TYPES.map((label) => (
                    <ScopeChip
                        key={label}
                        dim="tier"
                        label={label}
                        detail={MISSING_PRODUCT_TYPE}
                        selected={false}
                        disabled
                        onToggle={() => undefined}
                    />
                ))}
            </ScopeChipGroup>

            <ScopeChipGroup title="Gama" help={SALE_PROMOTION_HELP.scopeTier}>
                <AllScopeChip
                    dim="tier"
                    label="Todas"
                    selected={tiers.length === 0}
                    onClear={() => {
                        setValue('tiers', [], dirty);
                    }}
                />
                {PRODUCT_TIER_OPTIONS.map((option) => (
                    <ScopeChip
                        key={option.value}
                        dim="tier"
                        tone={PRODUCT_TIER_TONE}
                        label={option.label}
                        count={catalogReady ? (tierCounts.get(option.value) ?? 0) : undefined}
                        selected={tiers.includes(option.value)}
                        onToggle={() => {
                            setValue('tiers', toggleIn(tiers, option.value), dirty);
                        }}
                    />
                ))}
            </ScopeChipGroup>

            {multiChain ? (
                <>
                    <ScopeChipGroup
                        title="Producto (SKU)"
                        help={SALE_PROMOTION_HELP.scopeSku}
                        footer={<p className={MULTI_NOTE}>{MULTI_RETAILER_COPY.catalog}</p>}
                    >
                        <ScopeChipStatic dim="product" label="Todos" />
                    </ScopeChipGroup>
                    <ScopeChipGroup
                        title="Tienda"
                        help={SALE_PROMOTION_HELP.scopeStore}
                        footer={<p className={MULTI_NOTE}>{MULTI_RETAILER_COPY.stores}</p>}
                    >
                        <ScopeChipStatic dim="store" label="Todas" />
                    </ScopeChipGroup>
                </>
            ) : (
                <>
                    <SkuScopeGroup
                        withAllChip
                        help={SALE_PROMOTION_HELP.scopeSku}
                        searchLabel="Buscar producto del alcance"
                        products={products}
                        productsState={productsState}
                        selected={skus}
                        onChange={(next) => {
                            setValue('skus', next, dirty);
                        }}
                    />

                    <StoreScopeGroup
                        withAllChip
                        help={SALE_PROMOTION_HELP.scopeStore}
                        after={
                            <ScopeChip
                                dim="store"
                                label="Por canal"
                                detail={MISSING_CHANNEL}
                                selected={false}
                                disabled
                                onToggle={() => undefined}
                            />
                        }
                        searchLabel="Buscar tienda del alcance"
                        stores={stores}
                        storesState={storesState}
                        selected={storeIds}
                        onChange={(next) => {
                            setValue('storeIds', next, dirty);
                        }}
                    />
                </>
            )}
        </ScopeChipGrid>
    );
}

/** El alcance elegido en una línea, para la derecha del título de la sección. */
export function SalePromotionScopeSummary() {
    const { control } = useFormContext<SalePromotionFormValues>();
    const [productCategories, tiers, skus, storeIds] = useWatch({
        control,
        name: ['productCategories', 'tiers', 'skus', 'storeIds'],
    });
    return (
        <b className="font-semibold uppercase tracking-[0.04em] text-foreground">
            {describeScope({ productCategories, tiers, skus, storeIds })}
        </b>
    );
}
